import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Modal } from './Modal'
import { Field } from './Field'
import { Button } from './ui/button'
import { changePassword, getMyBusiness } from '../api/auth'
import { useDateFmt } from '../hooks/useDateFmt'
import type { AuthUser } from '../types'

// Extracted from Layout.tsx's top-bar user menu so the EnergyOS homeowner dashboard
// (EnergyOSDashboardLayout.tsx) can show "the same block as Rabbit Console" verbatim,
// instead of building separate account UI.
export function AccountProfileModal({ user, onClose }: { user: AuthUser | null; onClose: () => void }) {
  const fmt = useDateFmt()
  const initials = user?.fullName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase() ?? '?'

  // businessId is critical (it's the tenant boundary every request is scoped by) — the user
  // shown their own business's real details, not just its id, so a mismatch is easy to notice.
  const { data: business, isLoading: businessLoading } = useQuery({
    queryKey: ['my-business'],
    queryFn: () => getMyBusiness().then(r => r.data),
    enabled: !!user,
  })

  return (
    <Modal title="My profile" onClose={onClose} size="sm">
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
          <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-semibold">
            {initials}
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900">{user?.fullName}</p>
            <p className="text-xs text-slate-500">{user?.email}</p>
          </div>
        </div>
        {user?.roles && user.roles.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">Roles</p>
            <div className="flex flex-wrap gap-1.5">
              {user.roles.map((r) => (
                <span key={r} className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded border border-blue-100">
                  {r}
                </span>
              ))}
            </div>
          </div>
        )}
        <div>
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">Business</p>
          {businessLoading ? (
            <p className="text-xs text-slate-400">Loading…</p>
          ) : business ? (
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-slate-500 text-xs">Name</span>
                <span className="text-slate-900 font-medium truncate">{business.title}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-slate-500 text-xs">Business ID</span>
                <span className="text-slate-700 font-mono text-theme-xs truncate" title={business.id}>{business.id}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-slate-500 text-xs">Registration ID</span>
                <span className="text-slate-700 truncate">{business.registrationId}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-slate-500 text-xs">Default email</span>
                <span className="text-slate-700 truncate">{business.email}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-slate-500 text-xs">Status</span>
                <span className={business.isActive ? 'text-emerald-600' : 'text-red-500'}>
                  {business.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-slate-500 text-xs">Created</span>
                <span className="text-slate-700">{fmt(business.createdAt)}</span>
              </div>
            </div>
          ) : (
            <p className="text-xs text-red-500">Failed to load business details.</p>
          )}
        </div>
      </div>
    </Modal>
  )
}

export function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [fields,  setFields]  = useState({ current: '', next: '', confirm: '' })
  const [error,   setError]   = useState('')
  const [success, setSuccess] = useState(false)
  const [pending, setPending] = useState(false)

  const handleSubmit = async () => {
    if (!fields.next || !fields.current) { setError('All fields are required'); return }
    if (fields.next !== fields.confirm)   { setError('New passwords do not match'); return }
    if (fields.next.length < 8)           { setError('Minimum 8 characters'); return }
    setError(''); setPending(true)
    try {
      await changePassword(fields.current, fields.next)
      setSuccess(true)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to change password')
    } finally {
      setPending(false)
    }
  }

  return (
    <Modal title="Change password" onClose={onClose} size="sm">
      {success ? (
        <div className="text-center py-4 space-y-3">
          <p className="text-sm text-emerald-600 font-medium">Password changed successfully.</p>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {(['current', 'next', 'confirm'] as const).map((f) => (
            <Field key={f} label={f === 'current' ? 'Current password' : f === 'next' ? 'New password' : 'Confirm new password'}>
              <input type="password" value={fields[f]}
                onChange={(e) => setFields((p) => ({ ...p, [f]: e.target.value }))}
                className="field" />
            </Field>
          ))}
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={pending} loading={pending}>
              {pending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
